import React, { useEffect, useRef, useState } from 'react';
import { cx } from '../cx';
import { Calendar } from './Calendar';

// D-09 §15.14 — 날짜 선택 (트리거 인풋 + 캘린더 팝오버, z-index: --z-dropdown)
export interface DatePickerProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  value: string;
  onChange: (date: string) => void;
  label?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  /** 이 날짜 이전은 선택 불가 'YYYY-MM-DD' */
  minDate?: string;
  /** 이 날짜 이후는 선택 불가 'YYYY-MM-DD' */
  maxDate?: string;
  /** 비작업일 'YYYY-MM-DD' — 지정하면 달력에서 일요일과 이 날짜들을 빨강으로 표시한다 */
  holidays?: Set<string>;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(function DatePicker(
  { value, onChange, label, error, size = 'md', minDate, maxDate, holidays, placeholder = '날짜 선택', disabled, className = '', id, ...props },
  ref,
) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className={cx('field', error && 'error', className)}>
      {label && <label htmlFor={id}>{label}</label>}
      <div ref={rootRef} style={{ position: 'relative' }}>
        <input
          id={id}
          ref={ref}
          className={cx('input', size !== 'md' && `input-${size}`)}
          {...props}
          readOnly
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onClick={() => !disabled && setOpen((v) => !v)}
        />
        {open && (
          <div
            style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 'var(--z-dropdown)' } as React.CSSProperties}
          >
            <Calendar
              value={value}
              minDate={minDate}
              maxDate={maxDate}
              holidays={holidays}
              onChange={(d) => {
                onChange(d);
                setOpen(false);
              }}
            />
          </div>
        )}
      </div>
      {error && <span className="help">{error}</span>}
    </div>
  );
});
